import { createReducer, createActions } from 'reduxsauce'
import Immutable, { ImmutableObject } from 'seamless-immutable'
import { HabitsState } from '../Store/Habits/types'

/* ------------- Types and Action Creators ------------- */

type ImmutableHabitsState = ImmutableObject<HabitsState>

const actionDefs = {
  ensureNotificationParity: [],
  getCategories: [],
  getCategoriesSuccess: ['categories'],
  getCategoriesFailure: [],

  getTopics: ['id'],
  getTopicsSuccess: ['topics'],
  getTopicsFailure: [],

  getSingleHabit: ['id'],
  getSingleHabitSuccess: ['habit'],
  getSingleHabitFailure: [],

  getSingleHabitStats: ['id'],
  getSingleHabitStatsSuccess: ['id', 'stats'],
  getSingleHabitStatsFailure: ['id'],

  getSingleHabitProgress: ['id', 'fromDate'],
  getSingleHabitProgressSuccess: ['id', 'progress'],
  getSingleHabitProgressFailure: ['id'],

  getHabitList: ['date'],
  getHabitListSuccess: ['habits', 'progressStatsByHabit'],
  getHabitListFailure: [],

  getHabitContent: ['id'],
  getHabitContentSuccess: ['id', 'habit'],
  getHabitContentFailure: ['id'],

  startHabit: ['id', 'hasSupportingContent'],
  startHabitSuccess: ['id', 'deferredActions'],
  startHabitFailure: [],

  resetStartHabit: [],

  setHabitListDeferredActions: ['refresh', 'habitDeferredActions'],
  resetHabitListDeferredActions: [],

  removeHabit: ['id', 'date'],
  removeHabitSuccess: [],
  removeHabitFailure: [],
  resetRemoveHabit: [],

  doneHabit: ['id', 'date'],
  doneHabitSuccess: [],
  doneHabitFailure: [],

  resetDoneHabit: [],

  undoneHabit: ['id', 'date'],
  undoneHabitSuccess: [],
  undoneHabitFailure: [],

  resetUndoneHabit: [],

  getDailyProgressList: ['fromDate', 'toDate'],
  getDailyProgressListSuccess: ['dailyProgressList'],
  getDailyProgressListFailure: [],

  updateHabitPrompt: ['id', 'prompt', 'date'],
  updateHabitPromptSuccess: [],
  updateHabitPromptFailure: [],

  resetUpdateHabitPrompt: [],

  removeHabitPrompt: ['id', 'prompt', 'date'],
  removeHabitPromptSuccess: [],
  removeHabitPromptFailure: [],

  resetRemoveHabitPrompt: [],

  cancelAllHabitLocalNotifications: [],

  getAllNotificationConfigurations: [],
  getAllNotificationConfigurationsSuccess: ['notifications'],
  getAllNotificationConfigurationsFailure: [],

  getNotificationConfigurations: ['habitId'],
  getNotificationConfigurationsSuccess: ['habitId', 'notifications'],
  getNotificationConfigurationsFailure: ['habitId'],

  updateNotificationConfiguration: ['habitId', 'notificationType', 'values'],
  updateNotificationConfigurationSuccess: ['habitId', 'notification'],
  updateNotificationConfigurationFailure: [],

  resetUpdateNotificationConfiguration: [],
} as const satisfies Record<string, string[]>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const HabitTypes = Types
export default Creators

const HABIT_STATS_INITIAL_STATE = {
  data: undefined,
  loading: true,
  retry: false,
}
const HABIT_PROGRESS_INITIAL_STATE = {
  data: undefined,
  loading: true,
  retry: false,
}
export const INITIAL_STATE = Immutable<HabitsState>({
  categories: {
    loading: true,
    retry: false,
    data: [],
  },
  topics: {
    loading: true,
    retry: false,
    data: [],
  },
  habit: {
    loading: true,
    retry: false,
    data: {},
  },
  habitStats: {},
  habitProgress: {},
  habits: {
    loading: true,
    retry: false,
    data: [],
    progressStats: {},
  },
  habitContent: {},
  startHabit: {
    loading: false,
    retry: false,
    started: false,
  },
  habitListDeferredActions: null,
  removeHabit: {
    loading: false,
    retry: false,
    removed: false,
  },
  doneHabit: {
    loading: false,
    retry: false,
    id: null,
  },
  undoneHabit: {
    loading: false,
    retry: false,
    id: null,
  },
  dailyProgress: {
    loading: false,
    retry: false,
    data: [],
  },
  updateHabitPrompt: {
    loading: false,
    retry: false,
    updated: false,
    promptId: null,
  },
  removeHabitPrompt: {
    loading: false,
    retry: false,
    removed: false,
    promptId: null,
  },
  allNotificationConfigurations: {
    loading: false,
    retry: false,
    notifications: [],
  },
  notificationConfigurations: {},
  updateNotificationConfiguration: {
    habitId: undefined,
    type: undefined,
    loading: false,
    retry: false,
    updated: false,
  },
})

/* ------------- Reducers ------------- */

export const getCategories = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    categories: {
      loading: true,
      retry: false,
      data: [],
    },
  })

export const getCategoriesSuccess = (
  state: ImmutableHabitsState,
  { categories }: { categories: HabitsState['categories']['data'] }
): ImmutableHabitsState =>
  state.merge({
    categories: {
      loading: false,
      retry: false,
      data: categories,
    },
  })

export const getCategoriesFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    categories: {
      loading: false,
      retry: true,
    },
  })

export const getTopics = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    topics: {
      loading: true,
      retry: false,
      data: [],
    },
  })

export const getTopicsSuccess = (
  state: ImmutableHabitsState,
  { topics }: { topics: HabitsState['topics']['data'] }
): ImmutableHabitsState =>
  state.merge({
    topics: {
      loading: false,
      retry: false,
      data: topics,
    },
  })

export const getTopicsFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    topics: {
      loading: false,
      retry: true,
    },
  })

export const getSingleHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    habit: {
      loading: true,
      retry: false,
      data: {},
    },
  })

export const getSingleHabitSuccess = (
  state: ImmutableHabitsState,
  { habit }: { habit: HabitsState['habit']['data'] }
): ImmutableHabitsState =>
  state.merge({
    habit: {
      loading: false,
      retry: false,
      data: habit,
    },
  })

export const getSingleHabitFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    habit: {
      loading: false,
      retry: true,
    },
  })

export const getSingleHabitStats = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge(
    {
      habitStats: {
        [id]: HABIT_STATS_INITIAL_STATE,
      },
    },
    { deep: true }
  )

export const getSingleHabitStatsSuccess = (
  state: ImmutableHabitsState,
  { id, stats }: { id: string; stats: unknown }
): ImmutableHabitsState =>
  state.merge(
    {
      habitStats: {
        [id]: {
          loading: false,
          retry: false,
          data: stats,
        },
      },
    },
    { deep: true }
  )

export const getSingleHabitStatsFailure = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge(
    {
      habitStats: {
        [id]: {
          loading: false,
          retry: true,
        },
      },
    },
    { deep: true }
  )

export const getSingleHabitProgress = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge(
    {
      habitProgress: {
        [id]: HABIT_PROGRESS_INITIAL_STATE,
      },
    },
    { deep: true }
  )

export const getSingleHabitProgressSuccess = (
  state: ImmutableHabitsState,
  { id, progress }: { id: string; progress: unknown }
): ImmutableHabitsState =>
  state.merge(
    {
      habitProgress: {
        [id]: {
          loading: false,
          retry: false,
          data: progress,
        },
      },
    },
    { deep: true }
  )

export const getSingleHabitProgressFailure = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge(
    {
      habitProgress: {
        [id]: {
          loading: false,
          retry: true,
        },
      },
    },
    { deep: true }
  )

export const getHabitList = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    habits: {
      loading: true,
      retry: false,
      data: state.habits.data,
      progressStats: state.habits.progressStats,
    },
  })

export const getHabitListSuccess = (
  state: ImmutableHabitsState,
  {
    habits,
    progressStatsByHabit,
  }: {
    habits: HabitsState['habits']['data']
    progressStatsByHabit: HabitsState['habits']['progressStats']
  }
): ImmutableHabitsState => {
  return state.merge({
    habits: {
      loading: false,
      retry: false,
      data: habits,
      progressStats: progressStatsByHabit,
    },
  })
}

export const getHabitListFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    habits: {
      loading: false,
      retry: true,
      data: state.habits.data,
      progressStats: state.habits.progressStats,
    },
  })

export const getHabitContent = (
  state: ImmutableHabitsState,
  { id }: { id: string | undefined }
): ImmutableHabitsState => {
  if (!id) {
    return state
  }

  return state.merge(
    {
      habitContent: {
        [id]: {
          loading: true,
          retry: false,
          data: {},
        },
      },
    },
    { deep: true }
  )
}

export const getHabitContentSuccess = (
  state: ImmutableHabitsState,
  { id, habit }: { id: string | undefined; habit: Record<string, unknown> }
): ImmutableHabitsState => {
  if (!id) {
    return state
  }

  return state.merge(
    {
      habitContent: {
        [id]: {
          loading: false,
          retry: false,
          data: habit,
        },
      },
    },
    { deep: true }
  )
}

export const getHabitContentFailure = (
  state: ImmutableHabitsState,
  { id }: { id: string | undefined }
): ImmutableHabitsState => {
  if (!id) {
    return state
  }

  return state.merge(
    {
      habitContent: {
        [id]: {
          loading: false,
          retry: true,
          data: {},
        },
      },
    },
    { deep: true }
  )
}

export const startHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    startHabit: {
      loading: true,
      retry: false,
      started: false,
    },
    habitListDeferredActions: null,
  })

export const startHabitSuccess = (
  state: ImmutableHabitsState,
  { id, deferredActions }: { id: string; deferredActions: Record<string, unknown> }
): ImmutableHabitsState =>
  state.merge({
    startHabit: {
      loading: false,
      retry: false,
      started: true,
    },
    habitListDeferredActions: {
      refresh: true,
      habit: {
        id,
        ...deferredActions,
      },
    },
  })

export const startHabitFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    startHabit: {
      loading: false,
      retry: true,
      started: false,
    },
    habitListDeferredActions: null,
  })

export const resetStartHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    startHabit: INITIAL_STATE.startHabit,
  })

export const setHabitListDeferredActions = (
  state: ImmutableHabitsState,
  {
    refresh,
    habitDeferredActions,
  }: { refresh: boolean; habitDeferredActions: Record<string, unknown> }
): ImmutableHabitsState =>
  state.merge({
    habitListDeferredActions: {
      refresh,
      habit: habitDeferredActions,
    },
  })

export const resetHabitListDeferredActions = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    habitListDeferredActions: null,
  })

export const removeHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabit: {
      loading: true,
      retry: false,
      removed: false,
    },
  })

export const removeHabitSuccess = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabit: {
      loading: false,
      retry: false,
      removed: true,
    },
  })

export const removeHabitFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabit: {
      loading: false,
      retry: true,
      removed: false,
    },
  })

export const resetRemoveHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabit: INITIAL_STATE.removeHabit,
  })

export const doneHabit = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge({
    doneHabit: {
      loading: true,
      retry: false,
      id,
    },
  })

export const doneHabitSuccess = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    doneHabit: {
      loading: false,
      retry: false,
      id: state.doneHabit.id,
    },
  })

export const doneHabitFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    doneHabit: {
      loading: false,
      retry: true,
      id: null,
    },
  })

export const resetDoneHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    doneHabit: INITIAL_STATE.doneHabit,
  })

export const undoneHabit = (
  state: ImmutableHabitsState,
  { id }: { id: string }
): ImmutableHabitsState =>
  state.merge({
    undoneHabit: {
      loading: true,
      retry: false,
      id,
    },
  })

export const undoneHabitSuccess = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    undoneHabit: {
      loading: false,
      retry: false,
      id: null,
    },
  })

export const undoneHabitFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    undoneHabit: {
      loading: false,
      retry: true,
      id: null,
    },
  })

export const resetUndoneHabit = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    undoneHabit: INITIAL_STATE.undoneHabit,
  })

export const getDailyProgressList = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    dailyProgress: {
      loading: true,
      retry: false,
      data: state.dailyProgress.data,
    },
  })

export const getDailyProgressListSuccess = (
  state: ImmutableHabitsState,
  { dailyProgressList }: { dailyProgressList: HabitsState['dailyProgress']['data'] }
): ImmutableHabitsState =>
  state.merge({
    dailyProgress: {
      loading: false,
      retry: false,
      data: dailyProgressList,
    },
  })

export const getDailyProgressListFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    dailyProgress: {
      loading: false,
      retry: true,
      data: state.dailyProgress.data,
    },
  })

export const updateHabitPrompt = (
  state: ImmutableHabitsState,
  { prompt }: { prompt: { identifier: string } }
): ImmutableHabitsState =>
  state.merge({
    updateHabitPrompt: {
      loading: true,
      retry: false,
      updated: false,
      promptId: prompt.identifier,
    },
  })

export const updateHabitPromptSuccess = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    updateHabitPrompt: {
      loading: false,
      retry: false,
      updated: true,
      promptId: null,
    },
  })

export const updateHabitPromptFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    updateHabitPrompt: {
      loading: false,
      retry: true,
      updated: false,
      promptId: null,
    },
  })

export const resetUpdateHabitPrompt = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    updateHabitPrompt: INITIAL_STATE.updateHabitPrompt,
  })

export const removeHabitPrompt = (
  state: ImmutableHabitsState,
  { prompt }: { prompt: { identifier: string } }
): ImmutableHabitsState =>
  state.merge({
    removeHabitPrompt: {
      loading: true,
      retry: false,
      removed: false,
      promptId: prompt.identifier,
    },
  })

export const removeHabitPromptSuccess = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabitPrompt: {
      loading: false,
      retry: false,
      removed: true,
      promptId: null,
    },
  })

export const removeHabitPromptFailure = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabitPrompt: {
      loading: false,
      retry: true,
      removed: false,
      promptId: null,
    },
  })

export const resetRemoveHabitPrompt = (state: ImmutableHabitsState): ImmutableHabitsState =>
  state.merge({
    removeHabitPrompt: INITIAL_STATE.removeHabitPrompt,
  })

export const getAllNotificationConfigurations = (
  state: ImmutableHabitsState
): ImmutableHabitsState =>
  state.merge({
    allNotificationConfigurations: {
      loading: true,
      retry: false,
      notifications: [],
    },
  })

export const getAllNotificationConfigurationsSuccess = (
  state: ImmutableHabitsState,
  {
    notifications,
  }: { notifications: HabitsState['allNotificationConfigurations']['notifications'] }
): ImmutableHabitsState =>
  state.merge({
    allNotificationConfigurations: {
      loading: false,
      retry: false,
      notifications,
    },
  })

export const getAllNotificationConfigurationsFailure = (
  state: ImmutableHabitsState
): ImmutableHabitsState =>
  state.merge({
    allNotificationConfigurations: {
      loading: false,
      retry: true,
      notifications: [],
    },
  })

export const getNotificationConfigurations = (
  state: ImmutableHabitsState,
  { habitId }: { habitId: string }
): ImmutableHabitsState =>
  state.merge(
    {
      notificationConfigurations: {
        [habitId]: {
          data: undefined,
          loading: true,
          retry: false,
        },
      },
    },
    { deep: true }
  )

export const getNotificationConfigurationsSuccess = (
  state: ImmutableHabitsState,
  { habitId, notifications }: { habitId: string; notifications: unknown[] }
): ImmutableHabitsState =>
  state.merge(
    {
      notificationConfigurations: {
        [habitId]: {
          data: notifications,
          loading: false,
          retry: false,
        },
      },
    },
    { deep: true }
  )

export const getNotificationConfigurationsFailure = (
  state: ImmutableHabitsState,
  { habitId }: { habitId: string }
): ImmutableHabitsState =>
  state.merge(
    {
      notificationConfigurations: {
        [habitId]: {
          loading: false,
          retry: true,
        },
      },
    },
    { deep: true }
  )

export const updateNotificationConfiguration = (
  state: ImmutableHabitsState,
  { habitId, notificationType }: { habitId: string; notificationType: string }
): ImmutableHabitsState =>
  state.merge({
    updateNotificationConfiguration: {
      habitId,
      type: notificationType,
      loading: true,
      retry: false,
      updated: false,
    },
  })

export const updateNotificationConfigurationSuccess = (
  state: ImmutableHabitsState,
  { habitId, notification }: { habitId: string; notification: { type: string } }
): ImmutableHabitsState => {
  const prevNotifications = (state.notificationConfigurations[habitId] as { data?: unknown[] })
    ?.data
  let nextNotifications = prevNotifications

  const indexToUpdate = (nextNotifications || []).findIndex(
    (el) => (el as { type: string }).type === notification.type
  )
  if (indexToUpdate !== -1 && nextNotifications) {
    nextNotifications = [
      ...nextNotifications.slice(0, indexToUpdate),
      notification,
      ...nextNotifications.slice(indexToUpdate + 1),
    ]
  }

  return state.merge(
    {
      updateNotificationConfiguration: {
        loading: false,
        retry: false,
        updated: true,
      },
      notificationConfigurations: {
        [habitId]: {
          data: nextNotifications,
        },
      },
    },
    { deep: true }
  )
}

export const updateNotificationConfigurationFailure = (
  state: ImmutableHabitsState
): ImmutableHabitsState =>
  state.merge({
    updateNotificationConfiguration: {
      habitId: undefined,
      type: undefined,
      loading: false,
      retry: true,
      updated: false,
    },
  })

export const resetUpdateNotificationConfiguration = (
  state: ImmutableHabitsState
): ImmutableHabitsState =>
  state.merge({
    updateNotificationConfiguration: INITIAL_STATE.updateNotificationConfiguration,
  })
/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer<TPayloads, TActions, ImmutableHabitsState>(INITIAL_STATE, {
  [Types.GET_CATEGORIES]: getCategories,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_CATEGORIES_SUCCESS]: getCategoriesSuccess,
  [Types.GET_CATEGORIES_FAILURE]: getCategoriesFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_TOPICS]: getTopics,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_TOPICS_SUCCESS]: getTopicsSuccess,
  [Types.GET_TOPICS_FAILURE]: getTopicsFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT]: getSingleHabit,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_SUCCESS]: getSingleHabitSuccess,
  [Types.GET_SINGLE_HABIT_FAILURE]: getSingleHabitFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_STATS]: getSingleHabitStats,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_STATS_SUCCESS]: getSingleHabitStatsSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_STATS_FAILURE]: getSingleHabitStatsFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_PROGRESS]: getSingleHabitProgress,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_PROGRESS_SUCCESS]: getSingleHabitProgressSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_HABIT_PROGRESS_FAILURE]: getSingleHabitProgressFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HABIT_LIST]: getHabitList,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HABIT_LIST_SUCCESS]: getHabitListSuccess,
  [Types.GET_HABIT_LIST_FAILURE]: getHabitListFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HABIT_CONTENT]: getHabitContent,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HABIT_CONTENT_SUCCESS]: getHabitContentSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HABIT_CONTENT_FAILURE]: getHabitContentFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.START_HABIT]: startHabit,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.START_HABIT_SUCCESS]: startHabitSuccess,
  [Types.START_HABIT_FAILURE]: startHabitFailure,

  [Types.RESET_START_HABIT]: resetStartHabit,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SET_HABIT_LIST_DEFERRED_ACTIONS]: setHabitListDeferredActions,
  [Types.RESET_HABIT_LIST_DEFERRED_ACTIONS]: resetHabitListDeferredActions,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REMOVE_HABIT]: removeHabit,
  [Types.REMOVE_HABIT_SUCCESS]: removeHabitSuccess,
  [Types.REMOVE_HABIT_FAILURE]: removeHabitFailure,
  [Types.RESET_REMOVE_HABIT]: resetRemoveHabit,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.DONE_HABIT]: doneHabit,
  [Types.DONE_HABIT_SUCCESS]: doneHabitSuccess,
  [Types.DONE_HABIT_FAILURE]: doneHabitFailure,

  [Types.RESET_DONE_HABIT]: resetDoneHabit,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.UNDONE_HABIT]: undoneHabit,
  [Types.UNDONE_HABIT_SUCCESS]: undoneHabitSuccess,
  [Types.UNDONE_HABIT_FAILURE]: undoneHabitFailure,
  [Types.RESET_UNDONE_HABIT]: resetUndoneHabit,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_DAILY_PROGRESS_LIST]: getDailyProgressList,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_DAILY_PROGRESS_LIST_SUCCESS]: getDailyProgressListSuccess,
  [Types.GET_DAILY_PROGRESS_LIST_FAILURE]: getDailyProgressListFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.UPDATE_HABIT_PROMPT]: updateHabitPrompt,
  [Types.UPDATE_HABIT_PROMPT_SUCCESS]: updateHabitPromptSuccess,
  [Types.UPDATE_HABIT_PROMPT_FAILURE]: updateHabitPromptFailure,
  [Types.RESET_UPDATE_HABIT_PROMPT]: resetUpdateHabitPrompt,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REMOVE_HABIT_PROMPT]: removeHabitPrompt,
  [Types.REMOVE_HABIT_PROMPT_SUCCESS]: removeHabitPromptSuccess,
  [Types.REMOVE_HABIT_PROMPT_FAILURE]: removeHabitPromptFailure,
  [Types.RESET_REMOVE_HABIT_PROMPT]: resetRemoveHabitPrompt,

  [Types.GET_ALL_NOTIFICATION_CONFIGURATIONS]: getAllNotificationConfigurations,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_ALL_NOTIFICATION_CONFIGURATIONS_SUCCESS]: getAllNotificationConfigurationsSuccess,
  [Types.GET_ALL_NOTIFICATION_CONFIGURATIONS_FAILURE]: getAllNotificationConfigurationsFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_NOTIFICATION_CONFIGURATIONS]: getNotificationConfigurations,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_NOTIFICATION_CONFIGURATIONS_SUCCESS]: getNotificationConfigurationsSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_NOTIFICATION_CONFIGURATIONS_FAILURE]: getNotificationConfigurationsFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.UPDATE_NOTIFICATION_CONFIGURATION]: updateNotificationConfiguration,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.UPDATE_NOTIFICATION_CONFIGURATION_SUCCESS]: updateNotificationConfigurationSuccess,
  [Types.UPDATE_NOTIFICATION_CONFIGURATION_FAILURE]: updateNotificationConfigurationFailure,
  [Types.RESET_UPDATE_NOTIFICATION_CONFIGURATION]: resetUpdateNotificationConfiguration,
})
